import React, { useEffect, useState, useMemo } from "react";
import {
  Fire, Lightning, Crosshair, ArrowRight, ArrowsClockwise,
  LockKey, LockKeyOpen, Prohibit, CaretDown, ArrowDown, ArrowUp, X, Plus, Minus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  loadOverrides,
  patchPlayerOverride,
  clearPlayerOverride,
  OVERRIDES_CHANGED_EVENT,
} from "@/lib/overridesStore";

/**
 * Slate-Specific Pivot Board.
 *
 * Full-slate positional leverage view: for each skill position (QB / RB / WR / TE)
 * show the top chalk plays and, per chalk, the WarPaint-preferred same-position
 * pivot. Every row exposes inline controls that write into the shared player
 * overrides store (`wp_player_overrides` in localStorage) — so a click here
 * flows straight into the next Optimizer run without leaving the tab.
 *
 * Chalk-side actions: cap max exposure, exclude, nudge projection down, clear.
 * Pivot-side actions: lock, set min exposure, set max exposure, boost proj, clear.
 * Nothing is destructive — everything is togglable and the current state
 * badge (LOCK / EXC / MAX 15 / MIN 25 / -5% / +5%) is always visible on the row.
 */
const POSITIONS = ["QB", "RB", "WR", "TE"];

const POS_META = {
  QB: { color: "#FF8A00", label: "Quarterbacks" },
  RB: { color: "#39FF14", label: "Running Backs" },
  WR: { color: "#00B4FF", label: "Wide Receivers" },
  TE: { color: "#FF3B30", label: "Tight Ends" },
};

// Default projection-nudge magnitudes. Users get granular exposure control
// via the +/- stepper; the ±% boost is intentionally coarse so it stays a
// one-click "lean" rather than a full manual re-projection.
const CHALK_PROJ_DOWN = -5;    // % boost (i.e. -5% projection)
const PIVOT_PROJ_UP = 5;       // % boost

function OwnPill({ pct, tone }) {
  const val = pct === null || pct === undefined ? "—" : `${pct.toFixed(0)}%`;
  const color = tone === "chalk" ? "#FF3B30" : "#39FF14";
  return (
    <span
      className="mono text-[10px] font-black px-1.5 py-0.5 border"
      style={{
        color,
        borderColor: `${color}66`,
        background: `${color}12`,
        textShadow: `0 0 6px ${color}80`,
      }}
      title={tone === "chalk" ? "Projected ownership" : "Projected ownership (pivot)"}
    >
      {val}
    </span>
  );
}

// Compact badge that visualises the CURRENT state of a player's override so
// users always know what's already applied to that chalk / pivot.
function StateBadges({ ov }) {
  if (!ov) return null;
  const chips = [];
  if (ov.lock) chips.push(["LOCK", "#39FF14", "Player is locked into every Smart Optimizer lineup."]);
  if (ov.exclude) chips.push(["EXC", "#FF3B30", "Player is excluded from every Smart Optimizer lineup."]);
  if (ov.min_exposure_pct !== undefined && ov.min_exposure_pct !== "")
    chips.push([`MIN ${ov.min_exposure_pct}`, "#00B4FF", `Min exposure ${ov.min_exposure_pct}% — Smart Optimizer will roster this player in at least ${ov.min_exposure_pct}% of lineups.`]);
  if (ov.max_exposure_pct !== undefined && ov.max_exposure_pct !== "")
    // Sprint 60.141 — Explicit tooltip so users know this % is a
    // ceiling the Smart Optimizer enforces on generation, not just a
    // display label. Pivot Board patches propagate to Smart Optimizer
    // ONLY — Raw Optimizer has its own per-player min/max panel and
    // is intentionally decoupled from these overrides.
    chips.push([
      `MAX ${ov.max_exposure_pct}`, "#FF8A00",
      `Max exposure ${ov.max_exposure_pct}% (currently set by Smart Optimizer) — Smart Optimizer will roster this player in AT MOST ${ov.max_exposure_pct}% of lineups. Manual Optimizer ignores this; set caps on the Manual Pool table for that engine.`,
    ]);
  if (ov.boost_pct !== undefined && ov.boost_pct !== 0 && ov.boost_pct !== "") {
    const b = Number(ov.boost_pct);
    chips.push([
      `${b > 0 ? "+" : ""}${b}%`, b > 0 ? "#39FF14" : "#FF3B30",
      `Projection ${b > 0 ? "boost" : "fade"} of ${Math.abs(b)}% for Smart Optimizer builds. Manual Optimizer maintains its own boost column on the pool table.`,
    ]);
  }
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {chips.map(([text, color, title], i) => (
        <span
          key={i}
          title={title || undefined}
          className="mono text-[8px] font-black px-1 py-0.5 uppercase tracking-widest border cursor-help"
          style={{ color, borderColor: `${color}88`, background: `${color}14` }}
        >
          {text}
        </span>
      ))}
    </div>
  );
}

// Compact +/- stepper — dials any %. Value is stored as an integer in
// `min_exposure_pct` / `max_exposure_pct`. Passing 0 or null clears the
// override (via the shared clean pass in patchPlayerOverride).
function ExposureStepper({ label, value, onChange, accent, step = 5, min = 0, max = 100, tid }) {
  const active = value !== undefined && value !== null && value !== "";
  const v = active ? Number(value) : null;
  const bump = (delta) => {
    const cur = v == null ? (delta > 0 ? step : max) : v;
    const next = Math.max(min, Math.min(max, cur + delta));
    onChange(next === 0 ? null : next);
  };
  const clear = () => onChange(null);
  return (
    <div
      className={`flex items-center justify-between gap-1 border px-1.5 py-1 ${
        active ? "border-[color:var(--wp-accent)] bg-[color:var(--wp-accent)]/10" : "border-zinc-800"
      }`}
      style={{ ["--wp-accent"]: accent }}
      data-testid={tid}
    >
      <span className="mono text-[10px] uppercase tracking-widest text-zinc-400">{label}</span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => bump(-step)}
          disabled={!active && v == null}
          className="w-5 h-5 flex items-center justify-center border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white disabled:opacity-30"
          data-testid={`${tid}-dec`}
        >
          <Minus size={9} weight="bold" />
        </button>
        <span
          className="mono text-[11px] font-black tabular-nums w-9 text-center"
          style={{ color: active ? accent : "#71717a" }}
          data-testid={`${tid}-val`}
        >
          {active ? `${v}%` : "—"}
        </span>
        <button
          onClick={() => bump(step)}
          className="w-5 h-5 flex items-center justify-center border border-zinc-800 hover:border-zinc-600 text-zinc-400 hover:text-white"
          data-testid={`${tid}-inc`}
        >
          <Plus size={9} weight="bold" />
        </button>
        {active && (
          <button
            onClick={clear}
            className="w-5 h-5 flex items-center justify-center border border-zinc-800 hover:border-[#FF3B30]/60 text-zinc-500 hover:text-[#FF3B30] ml-0.5"
            title="Clear"
            data-testid={`${tid}-clear`}
          >
            <X size={9} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

// Small popover with the tone-appropriate action set. Chalk = fade actions,
// Pivot = boost/lock actions. Anchored right of the player row.
function ActionMenu({ open, onClose, tone, player, override, onApply }) {
  if (!open) return null;
  const isChalk = tone === "chalk";
  const accent = isChalk ? "#FF3B30" : "#39FF14";
  const Btn = ({ label, onClick, active, tid, disabled = false }) => (
    <button
      data-testid={tid}
      onClick={() => { onClick(); }}
      disabled={disabled}
      className={`text-left w-full px-2 py-1.5 text-[11px] mono border transition-colors ${
        active
          ? "border-[color:var(--wp-accent)] text-white bg-[color:var(--wp-accent)]/10"
          : "border-zinc-800 text-zinc-300 hover:border-zinc-600 hover:text-white"
      } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      style={{ ["--wp-accent"]: accent }}
    >
      {label}
    </button>
  );

  const ov = override || {};
  return (
    <div
      className="absolute right-0 top-full mt-1 z-30 w-60 bg-[#0a0a0a] border border-zinc-700 shadow-2xl p-2 space-y-1"
      onClick={(e) => e.stopPropagation()}
      data-testid={`action-menu-${tone}-${player.dk_id}`}
    >
      <div className="flex items-center justify-between px-1 pb-1 border-b border-zinc-800 mb-1">
        <div className="mono text-[9px] tracking-widest uppercase" style={{ color: accent }}>
          {isChalk ? "Fade the Chalk" : "Ride the Pivot"}
        </div>
        <button
          onClick={onClose}
          className="text-zinc-500 hover:text-white"
          data-testid={`action-menu-close-${tone}-${player.dk_id}`}
        >
          <X size={12} />
        </button>
      </div>
      {isChalk ? (
        <>
          <ExposureStepper
            label="Max Exposure"
            value={ov.max_exposure_pct}
            onChange={(v) => onApply({ max_exposure_pct: v })}
            accent={accent}
            tid={`chalk-max-stepper-${player.dk_id}`}
          />
          <Btn
            label={ov.boost_pct == CHALK_PROJ_DOWN ? `✓ Nudge Projection ${CHALK_PROJ_DOWN}%` : `Nudge Projection ${CHALK_PROJ_DOWN}%`}
            onClick={() => onApply({ boost_pct: ov.boost_pct == CHALK_PROJ_DOWN ? null : CHALK_PROJ_DOWN })}
            active={ov.boost_pct == CHALK_PROJ_DOWN}
            tid={`chalk-proj-down-${player.dk_id}`}
          />
          <Btn
            label={ov.exclude ? "✓ Exclude Player" : "Exclude Player"}
            onClick={() => onApply({ exclude: !ov.exclude, lock: false })}
            active={ov.exclude}
            tid={`chalk-exclude-${player.dk_id}`}
          />
          <Btn
            label={ov.lock ? "✓ Lock Anyway" : "Lock Anyway"}
            onClick={() => onApply({ lock: !ov.lock, exclude: false })}
            active={ov.lock}
            tid={`chalk-lock-${player.dk_id}`}
          />
        </>
      ) : (
        <>
          <Btn
            label={ov.lock ? "✓ Lock" : "Lock"}
            onClick={() => onApply({ lock: !ov.lock, exclude: false })}
            active={ov.lock}
            tid={`pivot-lock-${player.dk_id}`}
          />
          <Btn
            label={ov.boost_pct == PIVOT_PROJ_UP ? `✓ Boost Projection +${PIVOT_PROJ_UP}%` : `Boost Projection +${PIVOT_PROJ_UP}%`}
            onClick={() => onApply({ boost_pct: ov.boost_pct == PIVOT_PROJ_UP ? null : PIVOT_PROJ_UP })}
            active={ov.boost_pct == PIVOT_PROJ_UP}
            tid={`pivot-proj-up-${player.dk_id}`}
          />
          <ExposureStepper
            label="Min Exposure"
            value={ov.min_exposure_pct}
            onChange={(v) => onApply({ min_exposure_pct: v })}
            accent={accent}
            tid={`pivot-min-stepper-${player.dk_id}`}
          />
          <ExposureStepper
            label="Max Exposure"
            value={ov.max_exposure_pct}
            onChange={(v) => onApply({ max_exposure_pct: v })}
            accent="#FF8A00"
            tid={`pivot-max-stepper-${player.dk_id}`}
          />
          <Btn
            label={ov.exclude ? "✓ Uncheck / Exclude" : "Uncheck / Exclude"}
            onClick={() => onApply({ exclude: !ov.exclude, lock: false })}
            active={ov.exclude}
            tid={`pivot-exclude-${player.dk_id}`}
          />
        </>
      )}
      <div className="pt-1 border-t border-zinc-800 mt-1">
        <Btn
          label="Clear all overrides"
          onClick={() => onApply(null)}
          tid={`clear-${tone}-${player.dk_id}`}
          disabled={Object.keys(ov).length === 0}
        />
      </div>
    </div>
  );
}

function PlayerCell({ p, tone, override, onOpen, isOpen }) {
  const icon = tone === "chalk"
    ? <Fire size={12} weight="fill" className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
    : <Lightning size={12} weight="fill" className="text-[#39FF14] flex-shrink-0 mt-0.5" />;
  const disabled = !p.dk_id;
  return (
    <div className="flex items-start gap-2 min-w-0 flex-1 relative">
      {icon}
      <div className="min-w-0 flex-1">
        {/* Sprint 60.95 — Full name now gets its own row so the 4-column
            positional grid no longer clips it to "Anth…" / "Cars…". */}
        <div className="text-sm font-bold text-zinc-100 leading-tight break-words" title={p.name}>
          {p.name || "—"}
        </div>
        <div className="mono text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">
          {p.team}{p.opponent ? ` vs ${p.opponent}` : ""}
        </div>
        <div className="flex items-center gap-2 text-[10px] mono text-zinc-500 mt-1">
          <span>${(p.salary || 0).toLocaleString()}</span>
          <span className="text-zinc-700">·</span>
          <span>{(p.proj || 0).toFixed(1)} pts</span>
          {p.leverage !== undefined && (
            <>
              <span className="text-zinc-700">·</span>
              <span className="text-[#39FF14]" title="Leverage score = projection ÷ (ownership + 3)">
                lev {p.leverage.toFixed(2)}
              </span>
            </>
          )}
        </div>
        <StateBadges ov={override} />
      </div>
      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        <OwnPill pct={p.own} tone={tone} />
        <button
          onClick={onOpen}
          disabled={disabled}
          data-testid={`action-open-${tone}-${p.dk_id || p.name}`}
          title={disabled ? "No player id — actions unavailable" : "Adjust build"}
          className={`inline-flex items-center gap-0.5 mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${
            isOpen
              ? "border-zinc-500 text-white bg-zinc-800"
              : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
          } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
        >
          {tone === "chalk" ? "Fade" : "Ride"}
          <CaretDown size={9} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function PivotRow({ pair, accent, overrides, onApply, openKey, setOpenKey }) {
  const chalkKey = `chalk:${pair.chalk.dk_id || pair.chalk.name}`;
  const pivotKey = `pivot:${pair.pivot.dk_id || pair.pivot.name}`;
  const chalkOv = overrides[String(pair.chalk.dk_id || "")] || null;
  const pivotOv = overrides[String(pair.pivot.dk_id || "")] || null;

  return (
    <div
      className="grid grid-cols-[1fr_auto_1fr] items-start gap-2 border border-zinc-800 hover:border-zinc-700 bg-[#0e0e0e] px-3 py-2.5 transition-colors relative"
      data-testid={`pivot-row-${pair.chalk.name?.replace(/\s+/g, "-")}`}
    >
      <div className="relative min-w-0">
        <PlayerCell
          p={pair.chalk}
          tone="chalk"
          override={chalkOv}
          onOpen={(e) => { e?.stopPropagation?.(); setOpenKey(openKey === chalkKey ? null : chalkKey); }}
          isOpen={openKey === chalkKey}
        />
        <ActionMenu
          open={openKey === chalkKey}
          onClose={() => setOpenKey(null)}
          tone="chalk"
          player={pair.chalk}
          override={chalkOv}
          onApply={(patch) => onApply(pair.chalk, patch, "chalk")}
        />
      </div>
      <ArrowRight size={14} weight="bold" style={{ color: accent }} className="mt-2 flex-shrink-0 opacity-70" />
      <div className="relative min-w-0">
        <PlayerCell
          p={pair.pivot}
          tone="pivot"
          override={pivotOv}
          onOpen={(e) => { e?.stopPropagation?.(); setOpenKey(openKey === pivotKey ? null : pivotKey); }}
          isOpen={openKey === pivotKey}
        />
        <ActionMenu
          open={openKey === pivotKey}
          onClose={() => setOpenKey(null)}
          tone="pivot"
          player={pair.pivot}
          override={pivotOv}
          onApply={(patch) => onApply(pair.pivot, patch, "pivot")}
        />
      </div>
    </div>
  );
}

function PositionColumn({ pos, pairs, overrides, onApply, openKey, setOpenKey }) {
  const meta = POS_META[pos];
  return (
    <section
      className="bg-[#121212] border border-zinc-800 flex flex-col"
      data-testid={`pivot-board-${pos}`}
    >
      <header
        className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between"
        style={{ borderLeftWidth: 3, borderLeftColor: meta.color }}
      >
        <div className="flex items-center gap-2">
          <div className="mono text-[10px] tracking-[0.25em] font-black uppercase" style={{ color: meta.color }}>
            {pos}
          </div>
          <div className="overline text-zinc-500">{meta.label}</div>
        </div>
        <div className="mono text-[9px] tracking-widest text-zinc-600 uppercase">
          {pairs.length} pair{pairs.length === 1 ? "" : "s"}
        </div>
      </header>
      <div className="p-2 space-y-1.5 flex-1">
        {pairs.length === 0 ? (
          <div className="text-[11px] mono text-zinc-600 py-6 text-center">
            No chalk/pivot data for {pos} — pool may lack ownership signal.
          </div>
        ) : (
          pairs.map((p, i) => (
            <PivotRow
              key={`${pos}-${i}-${p.chalk.name}`}
              pair={p}
              accent={meta.color}
              overrides={overrides}
              onApply={onApply}
              openKey={openKey}
              setOpenKey={setOpenKey}
            />
          ))
        )}
      </div>
    </section>
  );
}

const POS_FILTER_KEY = "wp_pivot_board_pos_filter";

export default function PivotBoard({ perPosition = 4 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [overrides, setOverrides] = useState(() => loadOverrides());
  const [openKey, setOpenKey] = useState(null);
  // Position filter: "ALL" or one of QB/RB/WR/TE. Persisted so the user's
  // hero-stack focus survives tab navigation and slate re-loads.
  const [posFilter, setPosFilter] = useState(() => {
    try {
      const v = localStorage.getItem(POS_FILTER_KEY);
      return v && (v === "ALL" || POSITIONS.includes(v)) ? v : "ALL";
    } catch { return "ALL"; }
  });
  const selectFilter = (f) => {
    setPosFilter(f);
    try { localStorage.setItem(POS_FILTER_KEY, f); } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const d = await api.slatePivotBoard(perPosition);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load pivot board");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const onPoolRefresh = () => load();
    const onOverridesChanged = () => setOverrides(loadOverrides());
    window.addEventListener("wp:pool-refreshed", onPoolRefresh);
    window.addEventListener(OVERRIDES_CHANGED_EVENT, onOverridesChanged);
    // Close popover on outside click
    const onDoc = () => setOpenKey(null);
    window.addEventListener("click", onDoc);
    return () => {
      window.removeEventListener("wp:pool-refreshed", onPoolRefresh);
      window.removeEventListener(OVERRIDES_CHANGED_EVENT, onOverridesChanged);
      window.removeEventListener("click", onDoc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPosition]);

  // Apply a patch to a player's override entry, or clear all if patch === null.
  const applyPatch = (player, patch, tone) => {
    if (!player?.dk_id) {
      toast.error("Player has no DK id — can't apply override");
      return;
    }
    if (patch === null) {
      clearPlayerOverride(player.dk_id);
      toast.success(`Cleared overrides · ${player.name}`);
      return;
    }
    patchPlayerOverride(player.dk_id, patch);
    // Human-readable confirmation
    const bits = [];
    if ("lock" in patch) bits.push(patch.lock ? "locked" : "unlocked");
    if ("exclude" in patch) bits.push(patch.exclude ? "excluded" : "un-excluded");
    if ("boost_pct" in patch) bits.push(patch.boost_pct === null ? "cleared boost" : `${patch.boost_pct > 0 ? "+" : ""}${patch.boost_pct}% proj`);
    if ("min_exposure_pct" in patch) bits.push(patch.min_exposure_pct === null ? "cleared min-exp" : `min ${patch.min_exposure_pct}%`);
    if ("max_exposure_pct" in patch) bits.push(patch.max_exposure_pct === null ? "cleared max-exp" : `max ${patch.max_exposure_pct}%`);
    const verb = tone === "chalk" ? "Faded" : "Rode";
    toast.success(`${verb} · ${player.name}`, { description: bits.join(" · "), duration: 3500 });
  };

  const positions = data?.positions || { QB: [], RB: [], WR: [], TE: [] };
  const total = useMemo(
    () => POSITIONS.reduce((s, p) => s + (positions[p]?.length || 0), 0),
    [positions],
  );
  const isEmpty = !loading && total === 0;

  return (
    <section
      className="bg-[#0d0d0d] border border-zinc-800"
      data-testid="pivot-board"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Crosshair size={16} className="text-[#39FF14]" weight="fill" />
          <div className="heading font-bold">Pivot Board</div>
          <div
            className="overline text-zinc-500 ml-2"
            title="Fade/Ride and MIN/MAX exposure edits from the Pivot Board write to the Smart Optimizer's per-player override store ONLY. Manual Optimizer keeps its own per-player caps on the Manual Pool table and is intentionally decoupled from these controls."
          >
            Tap <span className="text-[#FF3B30]">Fade</span> to nudge a chalk or <span className="text-[#39FF14]">Ride</span> to boost its same-position pivot ·
            <span className="text-[#39FF14]"> Smart Optimizer only</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.source && data.source !== "empty" && (
            <div className="mono text-[9px] tracking-widest text-zinc-500 uppercase" title="Ownership signal source">
              src · {data.source === "our" ? "WarPaint Native" : "Market Consensus"}
            </div>
          )}
          <button
            onClick={load}
            data-testid="pivot-board-refresh"
            disabled={loading}
            className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-widest px-2 py-1 border border-zinc-800 hover:border-[#39FF14]/60 text-zinc-400 hover:text-[#39FF14] disabled:opacity-50"
          >
            <ArrowsClockwise size={11} weight="bold" className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Position filter chips — zoom the board to one position for
          deep-dive hero-stack planning. "ALL" restores the 4-column view. */}
      <div
        className="px-4 py-2 border-b border-zinc-800 flex items-center gap-2 flex-wrap"
        data-testid="pivot-board-filter-row"
      >
        <div className="mono text-[9px] tracking-widest text-zinc-500 uppercase mr-1">Focus</div>
        {(["ALL", ...POSITIONS]).map((f) => {
          const isActive = posFilter === f;
          const meta = POS_META[f];
          const count = f === "ALL"
            ? POSITIONS.reduce((s, p) => s + ((data?.positions?.[p] || []).length), 0)
            : (data?.positions?.[f] || []).length;
          const accent = meta?.color || "#a1a1aa";
          return (
            <button
              key={f}
              onClick={() => selectFilter(f)}
              data-testid={`pivot-board-chip-${f.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                isActive
                  ? "border-[color:var(--wp-accent)] text-white bg-[color:var(--wp-accent)]/15"
                  : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"
              }`}
              style={{ ["--wp-accent"]: accent }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: accent, boxShadow: isActive ? `0 0 6px ${accent}` : "none" }}
              />
              {f}
              <span className="mono text-[9px] tabular-nums opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="p-3">
        {loading && !data && (
          <div className="text-xs mono text-zinc-500 py-10 text-center">Building pivot board…</div>
        )}
        {err && (
          <div
            className="text-xs mono text-[#FF3B30] py-4 text-center border border-[#FF3B30]/30 bg-[#FF3B30]/5"
            data-testid="pivot-board-error"
          >
            {typeof err === "string" ? err : "Failed to load pivot board"}
          </div>
        )}
        {isEmpty && !err && (
          <div
            className="text-xs mono text-zinc-500 py-10 text-center"
            data-testid="pivot-board-empty"
          >
            No projections or ownership signal in this slate yet — load a slate
            and let the pool derive before checking the pivot board.
          </div>
        )}
        {!isEmpty && !err && (
          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: posFilter === "ALL"
                ? "repeat(auto-fit, minmax(360px, 1fr))"
                : "minmax(0, 1fr)",
            }}
          >
            {(posFilter === "ALL" ? POSITIONS : [posFilter]).map((pos) => (
              <PositionColumn
                key={pos}
                pos={pos}
                pairs={positions[pos] || []}
                overrides={overrides}
                onApply={applyPatch}
                openKey={openKey}
                setOpenKey={setOpenKey}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
